import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {Const, ResourceState, ResourceType, WizardApplicationType} from '../../../../core/helpers/Const';
import {ActivatedRoute} from '@angular/router';
import {HeaderTitleService} from '../../../../service/services/misc/header-title/header-title.service';
import {NavigationService} from '../../../../service/services/misc/navigation/navigation.service';
import {ResourcePageComponent} from '../../../../core/components/abstract/resource-page/resource-page.component';
import {Utils} from '../../../../core/helpers/Utils';
import {DialogService} from '../../../../service/services/misc/dialog/dialog.service';
import {TablePersonalizationDefinitions} from '../../../../core/helpers/TablePersonalizationDefinitions';
import {
    OrganizationHierarchyStoreService
} from '../../../../service/store/organization-hierarchy-store/organization-hierarchy-store.service';
import {CreateFolderDialogComponent} from '../../dialogs/create-folder-dialog/create-folder-dialog.component';
import {
    CreateResourceGroupDialogComponent
} from '../../dialogs/create-resource-group-dialog/create-resource-group-dialog.component';
import {AdvancedSearchFilters} from '../../../../shared/components/advanced-search/advanced-search.component';
import {ResourceDataService} from '../../../../service/services/rest/resource-data/resource-data.service';
import {EventService} from "../../../../service/services/misc/event/event.service";
import {UisService} from "../../../../service/services/misc/uis/uis.service";
import {SwaService} from "../../../../service/services/misc/swa/swa.service";
import {combineLatest, firstValueFrom} from "rxjs";
import {
    ExecutionType,
    UnifiedCreateApplicationDialogComponent
} from "../../dialogs/unified-create-application-dialog/unified-create-application-dialog.component";
import {
    UnifiedCreationFinishedDialogComponent
} from "../../dialogs/unified-create-application-dialog/unified-creation-finished-dialog/unified-creation-finished-dialog.component";
import {
    UnifiedDownloadFinishedDialogComponent
} from "../../dialogs/unified-create-application-dialog/unified-download-finished-dialog/unified-download-finished-dialog.component";
import {ResourceNotificationService} from "../../../../service/services/misc/fcl/resource-notification.service";
import {
    UnifiedCreateSuiteDialogComponent
} from "../../dialogs/unified-create-suite-dialog/unified-create-suite-dialog.component";
import {
    UnifiedSuiteCreationFinishedDialogComponent
} from "../../dialogs/unified-create-suite-dialog/unified-suite-creation-finished-dialog/unified-suite-creation-finished-dialog.component";
import {SavedFilterViewsService} from "../../../../service/services/misc/saved-filter-views/saved-filter-views.service";

@Component({
    selector: 'app-folder',
    templateUrl: './folder.component.html',
    styleUrls: ['./folder.component.scss'],
    standalone: false
})
export class FolderComponent extends ResourcePageComponent implements OnInit {
    ResourceType = ResourceType;
    Utils = Utils;
    Const = Const;
    ResourceState = ResourceState;
    Object = Object;

    get currentTableKey(): string {
        return this.advancedSearchOpen
            ? TablePersonalizationDefinitions.AdvancedSearchTable.key
            : TablePersonalizationDefinitions.ResourcesTable.key;
    }

    constructor(
        protected override _navigationService: NavigationService,
        private _route: ActivatedRoute,
        protected override _headerTitleService: HeaderTitleService,
        private dialogService: DialogService,
        private organizationHierarchyStore: OrganizationHierarchyStoreService,
        private resourceDataService: ResourceDataService,
        protected override cdRef: ChangeDetectorRef,
        protected override eventService: EventService,
        protected _uisService: UisService,
        protected _swaService: SwaService,
        protected override resourceNotificationService: ResourceNotificationService,
        savedFilterViewsService: SavedFilterViewsService
    ) {
        super(organizationHierarchyStore, _navigationService, dialogService, _headerTitleService, resourceDataService, cdRef, eventService, _uisService, _swaService, resourceNotificationService, savedFilterViewsService);
    }

    override ngOnInit() {
        super.ngOnInit();
        this.noDataFilteredMessage = 'No resources match your search query.';
        this.currentOrg = this._route.snapshot.paramMap.get('orgName');

        this.subscriptions.push(
            combineLatest([
                this._route.queryParams,
                this._route.params
            ]).subscribe(([queryParams, params]) => {
                const orgName = params[Const.ROUTE_PARAMETERS.ORGANIZATION];
                if (orgName) {
                    this.currentOrg = orgName;
                }
                this.loadPage({...queryParams, ...params});
            })
        );

    }

    async loadPage(params: any) {
        this._currentPath = params['path'] || '';
        if (this._currentPath === '') {
            this.currentResource = this.currentOrg;
            this.resourceType = ResourceType.ORGANIZATION;
        } else {
            this.currentResource = this._currentPath.split('/')[this._currentPath.split('/').length - 1];
            this.resourceType = ResourceType.FOLDER;
        }
        this.noDataMessage = this.getNoDataMessage();

        this._fullPath = `${this.currentOrg}${this._currentPath || ''}`;
        this.setParentPath(this._fullPath);

        // Close advanced search when navigating to a different location
        // This ensures the path column is removed if it was added by advanced search
        if (this.advancedSearchOpen) {
            this.advancedSearchOpen = false;
        }

        await this.setResourceTitles();
        await this.loadServices();
        if (Utils.hasFeatureFlag(Const.FEATURE_ADVANCED_SEARCH)) {
            await this.loadDiscoveryApiResources();
        }
    }

    getNoDataMessage() {
        if (this.resourceType === ResourceType.ORGANIZATION) {
            return "There are no folders or resource groups in this organization, or you don't have permissions to view the content of this organization.";
        }
        return "There are no resources in this folder, or you don't have permissions to access the content of this folder.";
    }

    createItem(type: string) {
        if (this.menu) {
            this.menu.close();
        }
        let path = '/' + this.currentOrg;
        if (this._currentPath) {
            path = path + this._currentPath;
        }

        let dialogComponent =
            type === ResourceType.FOLDER ? CreateFolderDialogComponent : CreateResourceGroupDialogComponent;

        const rules = this.systemResourcesByGroup.find(r => r.type === type)?.rules;
        const subresourceRules = this.systemResourcesByGroup.find(r => r.type === type)?.subresourceRules;

        let dialogRef = this.dialogService.openSideDialog(dialogComponent, {
            resource: {path: path, type: type, rules: rules, subresourceRules: subresourceRules},
            isEdit: false
        });

        dialogRef.afterClosed.subscribe({
            next: (res) => {
                this.resourceContent.createResource(res);
            },
        });
    }


    async onCreateApplication(applicationType: WizardApplicationType) {
        if (this.menu) {
            this.menu.close();
        }
        switch (applicationType) {
        case WizardApplicationType.UNIFIED:
            await this.openCreateUnifiedApplicationDialog();
            break;
        case WizardApplicationType.SUITE:
            await this.openCreateUnifiedSuiteDialog();
            break;
        default:
            await this.openCreateUnifiedApplicationDialog();
            break;
        }
    }

    private async openCreateUnifiedApplicationDialog() {
        let fullPath = '/' + this._fullPath;
        let dialogRef = this._dialogService.openSideDialog(
            UnifiedCreateApplicationDialogComponent,
            {
                path: fullPath,
                applicationResources: this.applicationResources
            },
            "62.5rem",
            false
        );

        try {
            const res = await firstValueFrom(dialogRef.afterClosed);
            if (res.executionType === ExecutionType.Download) {
                await this.openUnifiedDownloadFinishedDialog(res);
            } else {
                await this.openUnifiedCreationFinishedDialog(res, fullPath);
            }
        } catch (e) {
            //do nothing
        }
    }

    private async openCreateUnifiedSuiteDialog() {
        let fullPath = '/' + this._fullPath;
        let dialogRef = this._dialogService.openSideDialog(
            UnifiedCreateSuiteDialogComponent,
            {
                path: fullPath,
                applicationResources: this.applicationResources
            },
            "62.5rem",
            false
        );

        try {
            const res = await firstValueFrom(dialogRef.afterClosed);
            await this.openSuiteUnifiedCreationFinishedDialog(res, fullPath);
        } catch (e) {
            //do nothing
        }
    }

    private async openUnifiedDownloadFinishedDialog(res: any) {
        const downloadDialogRef = this._dialogService.openDialog(
            UnifiedDownloadFinishedDialogComponent,
            {
                res: res.res,
                payload: res.payload,
            },
            undefined,
            "32rem"
        );
        try {
            await firstValueFrom(downloadDialogRef.afterClosed);
        } catch (e) {
            //Do nothing
        }
    }

    private async openUnifiedCreationFinishedDialog(res: any, fullPath: string) {
        const creationDialogRef = this._dialogService.openDialog(
            UnifiedCreationFinishedDialogComponent,
            {
                res: res.res,
                payload: res.payload,
                operationalMode: res.operationalMode,
                isBTPApplication: res.isBTPApplication,
                operationalScenario: res.operationalScenario,
                subaccount: res.subaccount,
                templateSubaccount: res.templateSubaccount,
                appDisplayName: res.appDisplayName,
                executionType: res.executionType,
                path: fullPath,
            },
            undefined,
            "45rem"
        );
        try {
            await firstValueFrom(creationDialogRef.afterClosed);
        } finally {
            this.resourceContent.loadContent();
        }
    }

    private async openSuiteUnifiedCreationFinishedDialog(res: any, fullPath: string) {
        const creationDialogRef = this._dialogService.openDialog(
            UnifiedSuiteCreationFinishedDialogComponent,
            {
                res: res.res,
                payload: res.payload,
                step1: res.step1,
                path: fullPath,
            },
            undefined,
            "45rem"
        );
        try {
            await firstValueFrom(creationDialogRef.afterClosed);
        } finally {
            this.resourceContent.loadContent();
        }
    }

    get resultCount(): number {
        return this.resourceContent?.items?.length || 0;
    }

}
